import { useId, useMemo, useRef, useState } from 'react';

import { cx } from '../lib/cx';
import { useOnClickOutside } from '../lib/hooks';

import { Input } from './Input';

export interface ComboboxOption {
  value: string;
  label: string;
}

export interface ComboboxProps {
  /** Accessible label for the text input. */
  label: string;
  options: readonly ComboboxOption[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Shown when the query matches nothing. */
  emptyText?: string;
  hideLabel?: boolean;
  className?: string;
}

/**
 * A searchable single-select combobox (the category-picker base). Implements the
 * ARIA combobox/listbox pattern the design-reference §8.6.4 asks for: real
 * `aria-expanded`/`aria-controls`/`aria-activedescendant`, Up/Down/Enter/Escape
 * keyboard model, and filter-as-you-type.
 */
export function Combobox({
  label,
  options,
  value,
  onChange,
  placeholder,
  emptyText = 'No matches',
  hideLabel = false,
  className,
}: ComboboxProps): React.JSX.Element {
  const baseId = useId();
  const listId = `${baseId}-list`;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(rootRef, open, () => {
    setOpen(false);
  });

  const filtered = useMemo(
    () => options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase())),
    [options, query],
  );

  const selectAt = (index: number): void => {
    const option = filtered[index];
    if (option === undefined) return;
    onChange(option.value);
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, filtered.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter' && open) {
      event.preventDefault();
      selectAt(active);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const selectedLabel = options.find((o) => o.value === value)?.label ?? '';

  return (
    <div ref={rootRef} className={cx('ds-combobox', className)}>
      {hideLabel ? (
        <label className="sr-only" htmlFor={baseId}>
          {label}
        </label>
      ) : (
        <label className="ds-field__label" htmlFor={baseId}>
          {label}
        </label>
      )}
      <Input
        id={baseId}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && filtered.length > 0 ? `${listId}-${String(active)}` : undefined}
        placeholder={placeholder ?? selectedLabel}
        value={open ? query : selectedLabel}
        onFocus={() => {
          setOpen(true);
        }}
        onChange={(event) => {
          setQuery(event.currentTarget.value);
          setOpen(true);
          setActive(0);
        }}
        onKeyDown={onKeyDown}
      />
      {open ? (
        <ul className="ds-combobox__list" id={listId} role="listbox" aria-label={label}>
          {filtered.length === 0 ? (
            <li className="ds-combobox__empty">{emptyText}</li>
          ) : (
            filtered.map((option, index) => (
              <li
                key={option.value}
                id={`${listId}-${String(index)}`}
                role="option"
                aria-selected={option.value === value}
                className={cx('ds-combobox__option', index === active && 'ds-combobox__option--active')}
                onMouseDown={(event) => {
                  event.preventDefault();
                  selectAt(index);
                }}
                onMouseEnter={() => {
                  setActive(index);
                }}
              >
                {option.label}
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
